import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from '@engyalo/design-system';
import { useContext, useEffect, useState } from 'react';
import i18n, { LanguageTags } from 'i18n';
import { useNavigate } from 'react-router-dom';
import PreferencesContext from 'contexts/preferences/context';
import AuthContext from 'contexts/auth/context';
import BottomNavigation from 'components/BottomNavigation';
import AppHeader from 'components/AppHeader';
import ErrorModal from 'components/ErrorModal';
import useChangeStatus from 'hooks/useChangeStatus';
import { logError, logWarn } from 'services/Logger';
import { ISubscription } from 'types/webpush';
import { subscribeWebPush } from 'services/subscribeWebPush';
import { unSubscribeWebPush } from 'services/unsubscribeWebPush';
import { UserStatus } from 'types/user';
import { ModalLogout } from '../QueueMenu/ModalLogout';
import { ReactComponent as Loading } from '../../../assets/icons/loadingYalo.svg';
import SettingsItem from './SettingsItem';
import { CustomAvatar, SettingsWrapper, Wrapper, UserInfoWrapper, LoadingWrapper } from './style';

const Settings = () => {
  const { currentUserInfo } = useContext(AuthContext);
  const { selectedLanguage, handleSelectLanguage } = useContext(PreferencesContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadingPushNotification, setIsLoadingPushNotification] = useState(false);
  const [isPushNotificationEnabled, setIsPushNotificationEnabled] = useState(false);
  const [showModalLogout, setShowModalLogout] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const { setShowErrorModal, showErrorModal, errorMessageKey, changeStatus, isLoading } = useChangeStatus();

  const handleUserStatus = (isOnline: boolean) => {
    changeStatus(isOnline ? UserStatus.Online : UserStatus.Offline);
  };

  const handleClickSettings = () => {
    navigate('/mobile-settings');
  };

  const handleChatClick = () => {
    navigate('/');
  };

  const getRegistration: () => Promise<ServiceWorkerRegistration | undefined> = async () => {
    if (!('serviceWorker' in navigator)) {
      logWarn('Service Worker is not supported in this browser.');
      return;
    }
    const registration = await navigator.serviceWorker.getRegistration(
      `${window.location.origin}/workers/push-notification-worker.js`
    );
    if (!registration) {
      logWarn('Service Worker registration not found.');
      return;
    }
    return registration;
  };

  const handleSubscription = async () => {
    setIsLoadingPushNotification(true);

    const registration = await getRegistration();
    try {
      if (!registration?.pushManager) {
        throw new Error('PushManager is not active');
      }
      const subscriptionInfo = await registration?.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
      });
      if (!subscriptionInfo) {
        throw new Error('Failed to subscribe to push notifications');
      }
      const subscriptionToJson: ISubscription = subscriptionInfo?.toJSON();
      const subscriptionBody = {
        endpoint: subscriptionToJson.endpoint || '',
        keys: {
          auth: subscriptionToJson.keys?.auth || '',
          p256dh: subscriptionToJson.keys?.p256dh || '',
        },
        language: selectedLanguage.split('-')[0] || LanguageTags.DEFAULT,
      };
      const response = await subscribeWebPush(subscriptionBody);
      if (response.status !== 200) {
        throw new Error('Failed to subscribe to push notifications API');
      }
      setIsPushNotificationEnabled(true);
      setIsLoadingPushNotification(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logError('Error during push subscription:', errorMessage);
      setOpenErrorModal(true);
      if (registration?.pushManager) {
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      setIsPushNotificationEnabled(false);
      setIsLoadingPushNotification(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setIsLoadingPushNotification(true);
      const response = await unSubscribeWebPush();
      if (response.status !== 200) {
        throw new Error('Failed to unsubscribe from push notifications API');
      }
      const registration = await getRegistration();
      if (!registration?.pushManager) {
        throw new Error('PushManager is not active');
      }
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      setIsPushNotificationEnabled(false);
      setIsLoadingPushNotification(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logError('Error during push unSubscription:', errorMessage);
      setIsLoadingPushNotification(false);
      setOpenErrorModal(true);
      setIsPushNotificationEnabled(true);
    }
  };

  const handleClick = () => {
    if (!isPushNotificationEnabled) {
      handleSubscription();
    } else {
      handleUnsubscribe();
    }
  };

  const selectLanguage = (language: string) => {
    handleSelectLanguage(language);
    i18n.changeLanguage(language || LanguageTags.DEFAULT);
  };

  useEffect(() => {
    handleSelectLanguage(i18n.language);
  }, [i18n.language]);

  const initServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      logError('Push messaging is not supported in this browser.');
      return;
    }

    try {
      const registration = await getRegistration();
      const pushManager = registration?.pushManager;
      if (!pushManager) {
        logError('PushManager is not active');
        return;
      }

      // Optionally get an existing subscription
      const existingSub = await pushManager.getSubscription();
      if (existingSub) {
        setIsPushNotificationEnabled(true);
      }
    } catch (error) {
      setOpenErrorModal(true);
      logError('Error during Service Worker registration or push subscription:', error);
    }
  };

  useEffect(() => {
    // Call initialization for service worker/push subscriptions
    initServiceWorker();
  }, []);

  const handleErrorModalClose = () => {
    if (openErrorModal) {
      setOpenErrorModal(false);
    }
    if (showErrorModal) {
      setShowErrorModal(false);
    }
  };

  return (
    <Wrapper>
      <AppHeader
        sectionTitle={t('settings')}
        isUserOnline={currentUserInfo.status === UserStatus.Online}
        handleUserStatus={handleUserStatus}
      />

      {(isLoading || isLoadingPushNotification) && (
        <LoadingWrapper data-testid="loadingWrapper">
          <Loading />
        </LoadingWrapper>
      )}
      <UserInfoWrapper>
        <CustomAvatar variant="image" image={currentUserInfo.avatarUrl} />
        <Typography variant="h3" color="neutral.var80">
          {currentUserInfo.name}
        </Typography>
        <Typography variant="subtitle2" color="neutral.var80">
          {currentUserInfo.email}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<i className="fa-solid fa-arrow-right-from-bracket fa-xs" />}
          color="neutral2"
          onClick={() => setShowModalLogout(true)}
        >
          {t('exit')}
        </Button>
      </UserInfoWrapper>
      {showModalLogout && <ModalLogout showModalLogout={showModalLogout} setshowModalLogout={setShowModalLogout} />}
      <ErrorModal
        open={openErrorModal || showErrorModal}
        title={t('errorChangingLivechatStatus')}
        message={t(errorMessageKey, t('unexpectedError'))}
        handleClose={() => handleErrorModalClose()}
      />
      <SettingsWrapper>
        <Typography variant="h4" color="neutral.var50">
          {t('notifications')}
        </Typography>
        <Stack gap={1.5}>
          <SettingsItem
            icon="fa-regular fa-mobile"
            title={t('mobileNotifications')}
            description={t('notificationsInfo')}
            onClick={() => handleClick()}
            isActive={isPushNotificationEnabled}
          />
        </Stack>
        <Typography variant="h4" color="neutral.var50">
          {t('systemLanguage')}
        </Typography>
        <Stack gap={1.5}>
          <SettingsItem
            icon={<Typography>🇪🇸</Typography>}
            title="Español"
            onClick={() => selectLanguage(LanguageTags.ES)}
            isActive={selectedLanguage === LanguageTags.ES}
          />
          <SettingsItem
            icon={<Typography>🇺🇸</Typography>}
            title="English"
            onClick={() => selectLanguage(LanguageTags.EN)}
            isActive={selectedLanguage === LanguageTags.EN}
          />
          <SettingsItem
            icon={<Typography>🇧🇷</Typography>}
            title="Português (Brasil)"
            onClick={() => selectLanguage(LanguageTags.PT_BR)}
            isActive={selectedLanguage === LanguageTags.PT_BR}
          />
        </Stack>
      </SettingsWrapper>
      <Stack position="fixed" bottom="1.8rem" left="1.2rem" right="1.2rem" zIndex={1}>
        <BottomNavigation
          conversationAction={handleChatClick}
          settingsAction={handleClickSettings}
          currentActiveIndex={2}
        />
      </Stack>
    </Wrapper>
  );
};

export default Settings;
